import React from 'react';
import styled from 'styled-components';

import {
  getFirstName,
  typeToReadableSEOType,
  generateImageFromCDN,
  formatLocation,
  trackEvent,
} from '../helpers';

import Avatar from './Avatar';
import Button from './Button';
import EnquiryButton from './EnquiryButton';
import Location from './Location';
import PhoneNumber from './PhoneNumber';
import VerifiedBadge from './VerifiedBadge';

const MOBILE_BREAKPOINT = 600;

const Wrapper = styled.div`
  background: #fff;
  /* border-bottom: solid 2px #ccc; */
  margin: 1.25em 0;

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    position: sticky;
    top: -77px;
    z-index: 999;
  }
`;

const Background = styled.div`
  background: ${p => p.theme.muted.dull};
  background: #fff;
  border-radius: 8px;
  height: 140px;
  margin: 0 -50px;
  overflow: hidden;
  position: relative;
  z-index: 1;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin: 0 6px;
  }
`;

const BackgroundImage = styled.div`
  background: url(${p => generateImageFromCDN(p.src)}) center no-repeat;
  background-size: cover;
  filter: blur(15px);
  height: 150%;
  left: 0;
  opacity: 0.5;
  position: absolute;
  top: 0;
  width: 150%;
  z-index: 0;

  &:after {
    content: ' ';
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
    height: 140px;
    background: rgba(255, 255, 255, 0.7);
  }
`;

const Inner = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr 200px;
  grid-gap: 0.5em;
  margin: -85px 0 0 0;
  padding: 0 20px;
  position: relative;
  z-index: 5;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    text-align: center;
    grid-template-columns: 1fr;
  }
`;

const Name = styled.h1`
  align-self: flex-end;
  font-size: 2.2rem;
  margin: 0;

  > span {
    user-select: text;
    font-size: 0px;
    opacity: 0;
  }

  ${VerifiedBadge} {
    margin-left: 10px;
    margin-top: -12px;

    @media (max-width: ${MOBILE_BREAKPOINT}px) {
      position: absolute;
      margin: 0;
      top: -50px;
      width: 130px;
      right: 10px;
    }
  }
`;

const Meta = styled.div`
  align-self: flex-start;
  font-size: 14px;
`;

const CTAWrapper = styled.div`
  align-self: flex-end;
  display: flex;
  justify-content: flex-end;
  padding-top: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    justify-content: center;
    padding-top: 0;
  }

  button {
    margin: 0 4px;
  }
`;

export default function Billboard({ vendor, userId }) {
  const handlePhoneClick = () => {
    trackEvent({
      userId,
      vendorId: vendor.id,
      event: 'reveal-phone-number',
      category: 'All',
      label: vendor.id,
    });
  };

  const handleWebsiteClick = website => {
    trackEvent({
      userId,
      vendorId: vendor.id,
      event: 'open-vendor-website',
      category: 'All',
      label: vendor.id,
    });
    const formattedWebsite = website
      .replace('https://', '')
      .replace('http://', '');
    window.open(`https://${formattedWebsite}`, '_blank');
  };

  return (
    <Wrapper>
      <Background>
        {vendor.gallery && vendor.gallery?.length > 0 && (
          <BackgroundImage src={generateImageFromCDN(vendor.gallery[0].url)} />
        )}
      </Background>
      <Inner>
        <Avatar
          vendor={vendor}
          size={150}
          style={{ gridRowStart: '1', gridRowEnd: '3' }}
        />
        {vendor?.contacts?.length > 0 && (
          <Name>
            {getFirstName(vendor?.contacts[0]?.name)}
            <span>
              {' '}
              Say I do {typeToReadableSEOType(vendor.type, false)}{' '}
              {formatLocation(vendor.country, vendor.area_level_2)}{' '}
              {formatLocation(vendor.country, vendor.area_level_1)}
            </span>
            <VerifiedBadge>Say I do Verified</VerifiedBadge>
          </Name>
        )}
        <CTAWrapper>
          {vendor.detail?.website && (
            <Button
              onClick={e => {
                e.preventDefault();
                handleWebsiteClick(vendor.detail?.website);
              }}
              type="text"
            >
              View Website
            </Button>
          )}
          <EnquiryButton
            vendorId={vendor.id}
            userId={userId}
            type={vendor.type}
            location={vendor.area_level_1}
          />
        </CTAWrapper>
        <Meta>
          <div>{typeToReadableSEOType(vendor.type, false)}</div>
          <Location
            areaLevel1={vendor.area_level_1}
            areaLevel2={vendor.area_level_2}
            country={vendor.country}
          />
          <PhoneNumber
            number={vendor.phone}
            onClick={() => handlePhoneClick()}
          />
        </Meta>
      </Inner>
    </Wrapper>
  );
}
